<template>
  <div v-frag>
    <CardIeaAppMain>
      <v-col cols="12">
        <div class="text-h4 primary--text d-flex align-center flex-wrap px-4">
          Portal Docente
          <v-switch
            v-model="switchLegacyVersion"
            label="Volver a versión anterior"
            @change="handleSwitchLegacy"
            hide-details
            class="ml-4 mt-0 pt-0"
          ></v-switch>
        </div>
        <br />
        <v-row>
          <!-- COLUMNA IZQUIERDA -->
          <v-col cols="12" md="6">
            <!-- Panel Docente -->
            <v-card class="rounded-lg mb-4" elevation="2">
              <v-card-title class="panel-docente-header white--text py-2">
                Panel Docente
              </v-card-title>

              <!-- Mis Cursos Asignados -->
              <v-card
                class="mx-3 my-3 rounded-lg"
                color="#e3f2fd"
                elevation="0"
              >
                <v-card-title
                  class="cursos-header py-2 cursor-pointer"
                  @click="showCursos = !showCursos"
                >
                  <span class="font-weight-bold text-subtitle-1"
                    >Mis Cursos Asignados</span
                  >
                  <v-spacer />
                  <v-icon>{{
                    showCursos ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                </v-card-title>

                <v-expand-transition>
                  <div v-show="showCursos">
                    <v-divider />
                    <div v-if="cursosDocente.length > 0" class="pa-3">
                      <v-card
                        v-for="(curso, index) in cursosDocente"
                        :key="index"
                        class="mb-2 rounded-lg"
                        elevation="1"
                      >
                        <v-card-text
                          class="d-flex align-center justify-space-between py-3"
                        >
                          <span class="text-body-1 font-weight-medium">{{
                            curso.nombre
                          }}</span>
                          <v-btn
                            outlined
                            small
                            color="primary"
                            class="rounded-lg"
                            @click="gestionarCurso(curso)"
                          >
                            Gestionar
                          </v-btn>
                        </v-card-text>
                      </v-card>
                    </div>
                    <div v-else class="pa-4 text-center grey--text">
                      No tienes cursos asignados actualmente.
                    </div>
                  </div>
                </v-expand-transition>
              </v-card>
            </v-card>

            <!-- Correcciones Pendientes -->
            <v-card class="rounded-lg mb-4" elevation="2">
              <v-card-title class="correcciones-header white--text py-2">
                Correcciones Pendientes
              </v-card-title>
              <v-card-text class="pt-4">
                <div
                  v-for="(correccion, index) in correccionesPendientes"
                  :key="index"
                  class="d-flex align-center mb-3"
                >
                  <v-icon color="grey darken-1" class="mr-3"
                    >mdi-format-list-bulleted</v-icon
                  >
                  <span class="text-body-1">{{ correccion.texto }}</span>
                </div>
                <div
                  v-if="correccionesPendientes.length === 0"
                  class="text-center grey--text"
                >
                  No hay correcciones pendientes.
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- COLUMNA DERECHA -->
          <v-col cols="12" md="6">
            <!-- Calendario Académico -->
            <v-card class="rounded-lg mb-4" elevation="2">
              <v-card-title class="calendario-header py-2">
                <v-icon color="grey darken-2" class="mr-2">mdi-calendar</v-icon>
                Calendario Académico
              </v-card-title>
              <v-card-text class="pa-2">
                <div
                  class="d-flex align-center justify-space-between px-2 mb-2"
                >
                  <v-btn icon small @click="mesAnterior">
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <span class="text-h6 font-weight-medium text-capitalize">
                    {{ nombreMes }} {{ anioActual }}
                  </span>
                  <v-btn icon small @click="mesSiguiente">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
                <v-simple-table dense class="calendario-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th
                          v-for="dia in diasSemana"
                          :key="dia"
                          class="text-center calendario-th"
                        >
                          {{ dia }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(semana, sIndex) in semanasDelMes"
                        :key="sIndex"
                      >
                        <td
                          v-for="(dia, dIndex) in semana"
                          :key="dIndex"
                          class="text-center calendario-td"
                          :class="{
                            'dia-hoy': dia && esHoy(dia),
                            'dia-domingo': dia && esDomingo(sIndex, dIndex),
                            'dia-evento': dia && tieneEvento(dia)
                          }"
                        >
                          <v-tooltip bottom v-if="dia && tieneEvento(dia)">
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                v-on="on"
                                class="dia-con-evento"
                                >{{ dia }}</span
                              >
                            </template>
                            <div v-for="(ev, i) in getEventosDia(dia)" :key="i">
                              <strong>{{ ev.titulo }}</strong><br />
                              {{ ev.notas }}
                            </div>
                          </v-tooltip>
                          <span v-else-if="dia">{{ dia }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div class="d-flex flex-wrap align-center px-2 mt-2">
                  <v-chip x-small dark class="mr-2 mb-1" color="#636E72"
                    >1er Parcial</v-chip
                  >
                  <v-chip x-small dark class="mr-2 mb-1" color="#00897B"
                    >2do Parcial</v-chip
                  >
                  <v-chip x-small dark class="mr-2 mb-1" color="#FB8C00"
                    >Recuperatorio</v-chip
                  >
                  <v-chip x-small dark class="mr-2 mb-1" color="#8E24AA"
                    >Examen Final</v-chip
                  >
                </div>

                <!-- Próximos Eventos -->
                <div v-if="proximosEventos.length > 0" class="mt-3 px-2">
                  <div class="text-subtitle-2 font-weight-bold mb-2">
                    Próximos Eventos
                  </div>
                  <div
                    v-for="(evento, index) in proximosEventos"
                    :key="index"
                    class="d-flex align-center mb-2 pa-2 rounded"
                    :style="{
                      borderLeft: '4px solid ' + (evento.color || '#2D3436'),
                      backgroundColor: '#f9f9f9'
                    }"
                  >
                    <div class="flex-grow-1">
                      <div class="d-flex align-center mb-1">
                        <div class="text-body-2 font-weight-medium mr-2">
                          {{ evento.titulo }}
                        </div>
                        <v-chip
                          v-if="evento.tipo_examen"
                          x-small
                          dark
                          :color="evento.color || '#2D3436'"
                        >
                          {{ evento.tipo_examen }}
                        </v-chip>
                      </div>
                      <div class="text-caption grey--text">
                        {{ formatFecha(evento.inicio) }} -
                        {{ formatFecha(evento.fin) }}
                        <span v-if="evento.plataforma">
                          | {{ evento.plataforma }}</span
                        >
                      </div>
                      <div v-if="evento.notas" class="text-caption">
                        {{ evento.notas }}
                      </div>
                    </div>
                    <v-btn
                      v-if="evento.meet"
                      icon
                      small
                      color="green"
                      :href="
                        evento.meet.startsWith('http') ? evento.meet : null
                      "
                      target="_blank"
                      :title="evento.meet"
                    >
                      <v-icon small>mdi-video</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Mensajes y Consultas -->
            <v-card class="rounded-lg mb-4" elevation="2">
              <v-card-title class="mensajes-header white--text py-2">
                Mensajes y Consultas
              </v-card-title>
              <v-card-text class="pt-4">
                <div
                  class="d-flex align-center mb-3 cursor-pointer"
                  @click="irMensajes"
                >
                  <v-icon color="green darken-2" class="mr-3">mdi-email</v-icon>
                  <span class="text-body-1">
                    Mensajes no leídos
                    <v-chip
                      v-if="mensajesNoLeidos > 0"
                      small
                      color="red"
                      text-color="white"
                      class="ml-2"
                    >
                      {{ mensajesNoLeidos }}
                    </v-chip>
                  </span>
                </div>
                <div class="d-flex align-center cursor-pointer" @click="irForo">
                  <v-icon color="amber darken-2" class="mr-3">mdi-forum</v-icon>
                  <span class="text-body-1">
                    Posts de Foro
                    <v-chip
                      v-if="postsForoNuevos > 0"
                      small
                      color="orange"
                      text-color="white"
                      class="ml-2"
                    >
                      {{ postsForoNuevos }}
                    </v-chip>
                  </span>
                </div>
              </v-card-text>
            </v-card>

            <!-- Herramientas Docentes -->
            <v-card class="rounded-lg mb-4" elevation="2">
              <v-card-title class="herramientas-header white--text py-2">
                Herramientas Docentes
              </v-card-title>
              <v-card-text class="pt-4">
                <v-row>
                  <v-col cols="6">
                    <v-card
                      class="rounded-lg herramienta-card pa-3 text-center cursor-pointer"
                      color="#2D3436"
                      dark
                      elevation="1"
                      @click="irRecursos"
                    >
                      <v-icon large class="mb-1">mdi-book-open-variant</v-icon>
                      <div class="text-body-2 font-weight-medium">
                        Recursos Didácticos
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card
                      class="rounded-lg herramienta-card pa-3 text-center cursor-pointer"
                      color="#F9A825"
                      dark
                      elevation="1"
                      @click="irInformes"
                    >
                      <v-icon large class="mb-1">mdi-chart-bar</v-icon>
                      <div class="text-body-2 font-weight-medium">
                        Informes de Progreso
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </CardIeaAppMain>
  </div>
</template>

<script>
import CardIeaAppMain from "../../components/CardIeaAppMain";

export default {
  name: "HomeProfesor",
  components: {
    CardIeaAppMain
  },
  data() {
    return {
      showCursos: true,
      cursosDocente: [],
      correccionesPendientes: [],
      mensajesNoLeidos: 0,
      postsForoNuevos: 0,
      mesActual: new Date().getMonth(),
      anioActual: new Date().getFullYear(),
      diasSemana: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
      eventosCalendario: [],
      switchLegacyVersion: false
    };
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    nombreMes() {
      const meses = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre"
      ];
      return meses[this.mesActual];
    },
    semanasDelMes() {
      const primerDia = new Date(this.anioActual, this.mesActual, 1);
      const ultimoDia = new Date(this.anioActual, this.mesActual + 1, 0);
      const diasEnMes = ultimoDia.getDate();
      const diaInicio = primerDia.getDay();

      const semanas = [];
      let semanaActual = new Array(7).fill(null);

      for (let dia = 1; dia <= diasEnMes; dia++) {
        const posicion = (diaInicio + dia - 1) % 7;
        if (posicion === 0 && dia > 1) {
          semanas.push(semanaActual);
          semanaActual = new Array(7).fill(null);
        }
        semanaActual[posicion] = dia;
      }
      semanas.push(semanaActual);
      return semanas;
    },
    proximosEventos() {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      return this.eventosCalendario
        .filter(e => {
          const fin = new Date(e.fin + "T23:59:59");
          return fin >= hoy;
        })
        .sort((a, b) => new Date(a.inicio) - new Date(b.inicio))
        .slice(0, 5);
    }
  },
  methods: {
    esHoy(dia) {
      const hoy = new Date();
      return (
        dia === hoy.getDate() &&
        this.mesActual === hoy.getMonth() &&
        this.anioActual === hoy.getFullYear()
      );
    },
    esDomingo(semanaIndex, diaIndex) {
      return diaIndex === 0;
    },
    tieneEvento(dia) {
      return this.eventosCalendario.some(e => {
        const inicio = new Date(e.inicio);
        const fin = new Date(e.fin);
        const fecha = new Date(this.anioActual, this.mesActual, dia);
        inicio.setHours(0, 0, 0, 0);
        fin.setHours(0, 0, 0, 0);
        fecha.setHours(0, 0, 0, 0);
        return fecha >= inicio && fecha <= fin;
      });
    },
    getEventosDia(dia) {
      return this.eventosCalendario.filter(e => {
        const inicio = new Date(e.inicio);
        const fin = new Date(e.fin);
        const fecha = new Date(this.anioActual, this.mesActual, dia);
        inicio.setHours(0, 0, 0, 0);
        fin.setHours(0, 0, 0, 0);
        fecha.setHours(0, 0, 0, 0);
        return fecha >= inicio && fecha <= fin;
      });
    },
    formatFecha(fechaStr) {
      if (!fechaStr) return "";
      return fechaStr
        .split(" ")[0]
        .split("-")
        .reverse()
        .join("/");
    },
    mesAnterior() {
      if (this.mesActual === 0) {
        this.mesActual = 11;
        this.anioActual--;
      } else {
        this.mesActual--;
      }
    },
    mesSiguiente() {
      if (this.mesActual === 11) {
        this.mesActual = 0;
        this.anioActual++;
      } else {
        this.mesActual++;
      }
    },
    gestionarCurso(curso) {
      this.$router.push({
        name: "GestionCurso",
        params: {
          cursoId: curso.id,
          cursoNombre: curso.nombre.replace(/\./g, '')
        },
        query: {
          materia_id: curso.materia_id,
          materia_codigo_id: curso.materia_codigo_id
        }
      });
    },
    handleSwitchLegacy() {
      if (this.switchLegacyVersion) {
        this.goToLegacyVersion();
      }
    },
    goToLegacyVersion() {
      this.$router.push({ name: "Home", query: { legacy: "true" } });
    },
    irMensajes() {
      this.$router.push({ name: "ChatProfesor" });
    },
    irForo() {
      this.$router.push({ name: "foro" });
    },
    irRecursos() {
      // TODO: conectar con ruta de recursos didácticos cuando esté disponible
    },
    irInformes() {
      // TODO: conectar con ruta de informes de progreso cuando esté disponible
    },
    async cargarCursosDocente() {
      try {
        const res = await this.$API.get({
          url: "profesor-clases-v2",
          data: {}
        });
        if (res && res.data && Array.isArray(res.data)) {
          this.cursosDocente = res.data.map(c => ({
            id: c.id,
            materia_id: c.materia_id,
            materia_codigo_id: c.materia_codigo_id,
            nombre: c.materia || c.nombre || "Curso sin nombre",
            catedra_uuid: c.uuid,
            planEstudio: c.materia_IDStB,
            code: c.codClassRoom || c.code,
            ruta: c.ruta,
            google_drive: c.google_drive || c.url_google_drive
          }));
        }
      } catch (e) {
        console.warn("Error al cargar cursos del docente:", e);
      }
    },
    async cargarCorreccionesPendientes() {
      try {
        const res = await this.$API.get({
          url: "profesor/correcciones-pendientes",
          data: {}
        });
        if (res && Array.isArray(res)) {
          this.correccionesPendientes = res.map(c => ({
            texto: c.cantidad + " " + c.tipo + " - " + c.materia
          }));
        }
      } catch (e) {
        console.warn("Error al cargar correcciones pendientes:", e);
      }
    },
    async cargarMensajes() {
      try {
        const res = await this.$API.get({
          url: "profesor/mensajes-no-leidos",
          data: {}
        });
        if (res) {
          this.mensajesNoLeidos = res.mensajes || 0;
          this.postsForoNuevos = res.foros || 0;
        }
      } catch (e) {
        console.warn("Error al cargar mensajes:", e);
      }
    },
    async cargarEventosCalendario() {
      try {
        const [resCalendario, resExamenes] = await Promise.all([
          this.$API.get({
            url: "calendario-profesor",
            data: {}
          }),
          this.$API.get({
            url: "profesor/examenes-proximos-calendario",
            data: {}
          })
        ]);

        const eventosCalendario = Array.isArray(resCalendario)
          ? resCalendario.map(e => ({
              id: `cal-${e.id}`,
              titulo: e.titulo,
              meet: e.meet,
              notas: e.notas,
              estado: e.estado,
              inicio: e.inicio,
              fin: e.fin,
              tipo_examen: null,
              plataforma: e.plataforma,
              color: e.color || "#2D3436",
              horaDesde: e.horaDesde,
              horaHasta: e.horaHasta
            }))
          : [];

        const eventosExamenes = Array.isArray(resExamenes)
          ? resExamenes.map(e => ({
              id: `exa-${e.id}`,
              titulo: e.titulo,
              meet: e.meet,
              notas: e.notas,
              estado: e.estado,
              inicio: e.inicio,
              fin: e.fin,
              tipo_examen: e.tipo_examen,
              plataforma: e.plataforma,
              color: e.color || "#2D3436",
              horaDesde: e.horaDesde,
              horaHasta: e.horaHasta
            }))
          : [];

        this.eventosCalendario = [
          ...eventosCalendario,
          ...eventosExamenes
        ].sort((a, b) => new Date(a.inicio) - new Date(b.inicio));
      } catch (e) {
        console.warn("Error al cargar eventos y exámenes del calendario:", e);
      }
    },
    init() {
      if (this.user && this.user.tipoUsuario === "profesor") {
        this.cargarCursosDocente();
        this.cargarCorreccionesPendientes();
        this.cargarMensajes();
        this.cargarEventosCalendario();
      } else if (this.user && Object.keys(this.user).length > 0) {
        this.$router.push({ name: "Home" });
      }
    }
  },
  watch: {
    user: {
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          if (val.tipoUsuario !== "profesor") {
            this.$router.push({ name: "Home" });
          } else {
            this.init();
          }
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.user && Object.keys(this.user).length > 0) {
      this.init();
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.panel-docente-header {
  background-color: #5b9bd5;
  font-size: 1.1rem;
  font-weight: 600;
}

.cursos-header {
  background-color: #b3d4fc;
  border-radius: 8px 8px 0 0;
}

.correcciones-header {
  background-color: #c0392b;
  font-size: 1rem;
  font-weight: 600;
}

.calendario-header {
  background-color: #f5f5f5;
  font-size: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #e0e0e0;
}

.mensajes-header {
  background-color: #27ae60;
  font-size: 1rem;
  font-weight: 600;
}

.herramientas-header {
  background-color: #1a237e;
  font-size: 1rem;
  font-weight: 600;
}

.herramienta-card {
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.herramienta-card:hover {
  transform: translateY(-2px);
}

.calendario-table {
  width: 100%;
}

.calendario-th {
  background-color: #5b9bd5 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 8px 4px !important;
  font-size: 0.85rem !important;
}

.calendario-td {
  padding: 10px 4px !important;
  font-size: 0.9rem;
  border-bottom: 1px solid #e0e0e0;
}

.dia-hoy {
  background-color: #f57c00 !important;
  color: white !important;
  font-weight: bold;
  border-radius: 4px;
}

.dia-domingo {
  color: #e65100 !important;
  font-weight: 600;
}

.dia-evento {
  background-color: #fff3e0 !important;
  font-weight: 600;
}
</style>
